import { Option, Result } from '@sapphire/result';

import {
  McTraditionalSurveyPayloadInput,
  parseSurveyLocalization,
  Survey,
  SurveyLocaleData,
} from '@ps/survey-utils';

import {
  preDraftChildDraft,
  PreDraftChildDraftsInput,
} from './pre-draft-save.helper';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';

export type McTraditionalSurveyOutcome =
  | {
      readonly status: 'created';
      readonly locale: string;
      readonly isParent: boolean;
      readonly survey: Survey;
    }
  | { readonly status: 'rejected'; readonly locale: string };

export type McCreatedSurvey = Extract<
  McTraditionalSurveyOutcome,
  { status: 'created' }
>;

export function mcParentSurvey(created: readonly McCreatedSurvey[]): Survey {
  return (created.find(({ isParent }) => isParent) ?? created[0]).survey;
}

// Country comes from the child key, not the created survey: a create response
// without a `locale` block maps to an empty country code, grouping everything.
export function sameCountrySiblingIds(
  created: readonly McCreatedSurvey[],
  current: McCreatedSurvey,
): number[] {
  const { countryCode } = parseSurveyLocalization(current.locale);

  return created
    .filter(
      (candidate) =>
        candidate.survey.id !== current.survey.id &&
        parseSurveyLocalization(candidate.locale).countryCode === countryCode,
    )
    .map((candidate) => candidate.survey.id);
}

// Same rule as `sameCountrySiblingIds`, but these children carry a saved
// survey, so the country is read off it directly.
export function sameCountrySurveyIds(
  children: readonly SurveyChildEntry[],
  added: Survey,
): number[] {
  return children.flatMap(([, , { survey }]) =>
    Option.from(survey)
      .filter(
        ({ id, countryCode }) =>
          id !== added.id && countryCode === added.countryCode,
      )
      .mapOr<number[], number[]>([], ({ id }) => [id]),
  );
}

export function addedLocaleInput(
  source: SurveyChildEntry,
  context: PreDraftChildDraftsInput,
  locale: SurveyLocaleData,
): Result<McTraditionalSurveyPayloadInput, string> {
  const [, , { survey }] = source;

  return Option.from(survey?.mcUniqueId).match({
    some: (mcUniqueId) =>
      preDraftChildDraft(source, context).map(({ input }) => ({
        ...input,
        metadata: {
          ...input.metadata,
          selectedCountries: [locale.countryCode],
          selectedLanguages: [locale.languageShortCode],
        },
        mcUniqueId,
        isParent: false,
      })),
    none: () => Result.err(context.invalidChildMessage),
  });
}
